import React, { useState, useRef, useEffect } from "react";
import { FaUser, FaEnvelope, FaIdBadge, FaBuilding, FaSignOutAlt } from "react-icons/fa";

export default function ProfileMenu({ name, email, role, unitNbr, onSignOut }) {
  const [open, setOpen] = useState(false);
  const menuRef = useRef(null);

  // Close on outside tap
  useEffect(() => {
    if (!open) return;
    function handleClick(e) {
      if (menuRef.current && !menuRef.current.contains(e.target)) setOpen(false);
    }
    document.addEventListener("mousedown", handleClick);
    document.addEventListener("touchstart", handleClick);
    return () => {
      document.removeEventListener("mousedown", handleClick);
      document.removeEventListener("touchstart", handleClick);
    };
  }, [open]);

  // Build initials from name
  const initials = (() => {
    const parts = (name || "").trim().split(/\s+/);
    if (parts.length >= 2) return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
    if (parts[0]) return parts[0][0].toUpperCase();
    return "U";
  })();

  const roleLabel = (role || "").charAt(0).toUpperCase() + (role || "").slice(1);

  return (
    <div ref={menuRef} className="relative">
      {/* Avatar button */}
      <button
        onClick={() => setOpen((v) => !v)}
        className="w-10 h-10 rounded-full bg-white/25 hover:bg-white/35 active:bg-white/45 flex items-center justify-center text-white font-semibold text-sm transition-colors flex-shrink-0"
        aria-label="Profile menu"
      >
        {initials}
      </button>

      {/* Dropdown */}
      {open && (
        <>
          {/* Backdrop for mobile */}
          <div className="fixed inset-0 z-[69]" onClick={() => setOpen(false)} />

          <div className="absolute right-0 top-full mt-2 w-72 bg-white rounded-xl shadow-2xl z-[70] overflow-hidden animate-in">
            {/* Profile header */}
            <div className="bg-gradient-to-r from-blue-600 to-blue-400 px-5 py-5">
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 rounded-full bg-white/20 flex items-center justify-center text-white text-lg font-bold border-2 border-white/40">
                  {initials}
                </div>
                <div className="min-w-0 flex-1">
                  <p className="text-white font-semibold text-sm truncate">{name || "User"}</p>
                  <p className="text-blue-100 text-xs truncate">{email || ""}</p>
                </div>
              </div>
            </div>

            {/* Details */}
            <div className="px-5 py-3 space-y-3">
              <div className="flex items-center gap-3">
                <FaEnvelope className="text-gray-400 flex-shrink-0" size={14} />
                <div className="min-w-0">
                  <p className="text-[11px] text-gray-400 uppercase tracking-wide">Email</p>
                  <p className="text-sm text-gray-800 truncate">{email || "—"}</p>
                </div>
              </div>

              <div className="flex items-center gap-3">
                <FaIdBadge className="text-gray-400 flex-shrink-0" size={14} />
                <div className="min-w-0">
                  <p className="text-[11px] text-gray-400 uppercase tracking-wide">Role</p>
                  <p className="text-sm text-gray-800">{roleLabel || "—"}</p>
                </div>
              </div>

              {unitNbr && (
                <div className="flex items-center gap-3">
                  <FaBuilding className="text-gray-400 flex-shrink-0" size={14} />
                  <div className="min-w-0">
                    <p className="text-[11px] text-gray-400 uppercase tracking-wide">Unit</p>
                    <p className="text-sm text-gray-800">{unitNbr}</p>
                  </div>
                </div>
              )}
            </div>

            {/* Sign out */}
            <div className="border-t border-gray-100 px-5 py-3">
              <button
                onClick={() => { setOpen(false); onSignOut(); }}
                className="w-full flex items-center justify-center gap-2 py-2.5 rounded-lg bg-red-50 hover:bg-red-100 active:bg-red-200 text-red-600 font-medium text-sm transition-colors"
              >
                <FaSignOutAlt size={14} />
                Sign Out
              </button>
            </div>
          </div>
        </>
      )}
    </div>
  );
}
